// src/hooks/useStoryReactions.js - Fixed version
import { useState, useEffect, useRef } from 'react';
import { db } from '../config/firebase';
import { 
  collection, query, where, orderBy, onSnapshot, 
  limit, getDocs 
} from 'firebase/firestore';

export const useStoryReactions = (albumId, storyId) => {
  const [reactions, setReactions] = useState([]);
  const [liveReactions, setLiveReactions] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const unsubscribeRef = useRef(null);
  const isSubscribedRef = useRef(false);
  const lastReactionCountRef = useRef(0);

  useEffect(() => {
    if (!albumId || !storyId) {
      setLoading(false);
      return;
    }

    // Clean up any existing listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    isSubscribedRef.current = true;

    const setupListener = async () => {
      try {
        console.log('🔥 Setting up story reactions listener for:', { albumId, storyId });
        
        // Use a simpler query structure to avoid Firestore conflicts
        const reactionsRef = collection(db, 'pb-album-reactions');
        const reactionsQuery = query(
          reactionsRef,
          where('bookId', '==', albumId),
          where('storyId', '==', storyId),
          limit(100) // Reasonable limit
        );

        unsubscribeRef.current = onSnapshot(
          reactionsQuery,
          {
            // Add error handling to the listener
            includeMetadataChanges: false // Simplify to avoid metadata conflicts
          },
          (snapshot) => {
            if (!isSubscribedRef.current) return;

            try {
              const allReactions = [];
              const newLiveReactions = [];
              let commentsData = [];

              snapshot.docs.forEach(doc => {
                const reactionData = { id: doc.id, ...doc.data() };
                allReactions.push(reactionData);

                // Separate comments
                if (reactionData.type === 'comment') {
                  commentsData.push(reactionData);
                }
              });

              // Sort reactions by timestamp (client-side to avoid query conflicts)
              allReactions.sort((a, b) => {
                const aTime = a.timestamp?.toDate?.() || new Date(a.timestamp || 0);
                const bTime = b.timestamp?.toDate?.() || new Date(b.timestamp || 0);
                return bTime - aTime;
              });

              // Sort comments chronologically for display
              commentsData.sort((a, b) => {
                const aTime = a.timestamp?.toDate?.() || new Date(a.timestamp || 0);
                const bTime = b.timestamp?.toDate?.() || new Date(b.timestamp || 0);
                return aTime - bTime;
              });

              // Detect new reactions for animations (only after initial load)
              if (lastReactionCountRef.current > 0 && allReactions.length > lastReactionCountRef.current) {
                const newReactions = allReactions.slice(0, allReactions.length - lastReactionCountRef.current);
                newLiveReactions.push(...newReactions);
                
                console.log('🎉 New live reactions detected:', newReactions.length);
              }

              setReactions(allReactions);
              setComments(commentsData);
              
              if (newLiveReactions.length > 0) {
                setLiveReactions(prev => [...prev, ...newLiveReactions]);
                
                // Clean up live reactions after animation
                setTimeout(() => {
                  setLiveReactions(prev => 
                    prev.filter(reaction => !newLiveReactions.some(newR => newR.id === reaction.id))
                  );
                }, 3000);
              }

              lastReactionCountRef.current = allReactions.length;
              setLoading(false);
              setError(null);

            } catch (processingError) {
              console.error('🔥 Error processing snapshot:', processingError);
              setError(processingError);
            }
          },
          (firestoreError) => {
            console.error('🔥 Firestore listener error:', firestoreError);
            setError(firestoreError);
            setLoading(false);
            
            // Try to recover with a simple fetch instead of real-time
            if (isSubscribedRef.current) {
              console.log('🔄 Falling back to static fetch...');
              fallbackToStaticFetch();
            }
          }
        );

        console.log('✅ Story reactions listener setup complete');

      } catch (setupError) {
        console.error('🔥 Error setting up listener:', setupError);
        setError(setupError);
        setLoading(false);
        fallbackToStaticFetch();
      }
    };

    // Fallback function for static data fetching
    const fallbackToStaticFetch = async () => {
      try {
        console.log('📡 Fetching reactions statically...');
        const reactionsRef = collection(db, 'pb-album-reactions');
        const reactionsQuery = query(
          reactionsRef,
          where('bookId', '==', albumId),
          where('storyId', '==', storyId),
          limit(50)
        );

        const snapshot = await getDocs(reactionsQuery);
        const reactionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const commentsData = reactionsData.filter(r => r.type === 'comment');
        
        setReactions(reactionsData);
        setComments(commentsData);
        setLoading(false);
        
      } catch (fetchError) {
        console.error('🔥 Fallback fetch failed:', fetchError);
        setError(fetchError);
        setLoading(false);
      }
    };

    setupListener();

    return () => {
      console.log('🧹 Cleaning up story reactions listener');
      isSubscribedRef.current = false;
      
      if (unsubscribeRef.current) {
        try {
          unsubscribeRef.current();
        } catch (cleanupError) {
          console.warn('Warning: Error during listener cleanup:', cleanupError);
        }
        unsubscribeRef.current = null;
      }
      
      lastReactionCountRef.current = 0;
    };
  }, [albumId, storyId]);

  // Utility functions
  const getLikeCount = () => reactions.filter(r => r.type === 'like').length;
  const getCommentCount = () => reactions.filter(r => r.type === 'comment').length;
  const getAudioReactionCount = () => reactions.filter(r => r.type === 'audio').length;
  const getPhotoReactionCount = () => reactions.filter(r => r.type === 'photo').length;

  return {
    reactions,
    liveReactions,
    comments,
    loading,
    error,
    getLikeCount,
    getCommentCount,
    getAudioReactionCount,
    getPhotoReactionCount
  };
};